import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import LocalIncidentsProcessedByWmrcTable from './Table/LocalIncidentsProcessedByWmrc'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import axios from 'axios'
import { useSelector } from 'react-redux'
import toasters from '../../../../../../../utils/toasters/Toasters'
import { Pagination } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'

const formatPayloadDates = (payload) => {
    if (!payload) return payload;

    const formattedPayload = { ...payload };

    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate);
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0];
        }
    }

    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate);
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0];
        }
    }

    return formattedPayload;
};

const LocalIncidentsProcessedByWmrc = () => {
    const location = useLocation();
    const { token } = useSelector((state) => state?.auth);
    const [sort, setSort] = useState(null);
    const queryClient = useQueryClient();

    const [searchParams] = useSearchParams();
    const [payload, setPayload] = useState(null);

    useEffect(() => {

        //  Read from URL params
        const startDate = searchParams.get('startDate');
        const endDate = searchParams.get('endDate');
        const code = searchParams.get('code');

        if (startDate || endDate) {
            const urlPayload = { startDate, endDate, code };
            setPayload(urlPayload);
            sessionStorage.setItem(
                'LocalIncidentsProcessedByWmrcPayload',
                JSON.stringify(urlPayload)
            );
            return;
        }

        // fallback to location.state
        if (location.state) {
            setPayload(location.state);
            sessionStorage.setItem(
                'LocalIncidentsProcessedByWmrcPayload',
                JSON.stringify(location.state)
            );
            return;
        }

        // fallback to sessionStorage
        const storedPayload = sessionStorage.getItem(
            'LocalIncidentsProcessedByWmrcPayload'
        );

        if (storedPayload) {
            setPayload(JSON.parse(storedPayload));
        }

    }, [location.state, searchParams]);


    const formattedPayload = formatPayloadDates(payload);

    const getIncidentAnalysisData = useCallback(async () => {
        let apiQueryParams = {};

        if (sort && sort?.columnName && sort.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort.order,
                sort: sort?.columnName,
            };
        }

        return fetchIncidentAnalysisData({
            ...formattedPayload,
            ...apiQueryParams,
            export: 'N'
        });
    }, [sort, formattedPayload]);

    const handleColumnSort = async (column, sortDirection) => {
        setSort((prev) => ({
            ...prev,
            columnName: column?.sortField,
            order: sortDirection,
        }));
    };

    const {
        data,
        isLoading,
        isError,
        error,
      } = useQuery(
        ['fetch-local-incident-processed-wmrc', payload, sort],
        getIncidentAnalysisData,
        {
             enabled: !!formattedPayload,
            select: (response) => {
                const data = Object.values(response)?.filter(
                  (item) => typeof item === 'object'
                );
              
                const totalIncidents = data?.reduce(
                  (sum, item) => sum + item?.new_incidents,
                  0
                );
                const totalIncidentClosed = data?.reduce(
                  (sum, item) => sum + item?.incident_closed,
                  0
                );
                const totalRemainingIncidents = data?.reduce(
                  (sum, item) => sum + item?.remaining_incidents,
                  0
                );
              
                const obj = {
                  name_tx: 'Total',
                  new_incidents: totalIncidents,
                  incident_closed: totalIncidentClosed,
                  remaining_incidents: totalRemainingIncidents,
                };
              
                return {
                  data: [...data, obj],
                  newIncidentsSum: response?.newIncidentsSum ?? 0,
                };
              },              
          retry: (failureCount, error) => {
            const status = error?.response?.status;
            return failureCount < 1 && status >= 400 && status < 600; 
          },
          retryDelay: 1000,         }
      );
      

    const tableColumn = [
        'Congregations',
        'Total Incidents',
        'Incident Closed',
        'Open Incident',
    ];

   const downloadPDF = () => {
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [297, 210],
    });

    doc.setFontSize(18);
    doc.text('Local Incidents Processed By WMRC Report', 14, 22);

    doc.setFontSize(12);
    doc.text(
        `Report Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
        14,
        30
    );

    const tableRows =
        data?.data?.map((item) => [
            item?.name_tx || '',
            item?.new_incidents || '',
            item?.incident_closed || '',
            item?.remaining_incidents || '',
        ]) || [];

    doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 40,
        // 1. Set margins to match your title text (14mm)
        margin: { left: 14, right: 14 },
        // 2. Force table to fill available space between margins
        tableWidth: 'auto', 
        theme: 'grid',
        headStyles: {
            fillColor: [0, 81, 242],
            textColor: [255, 255, 255],
            fontSize: 12,
            halign: 'center' // Centered headers look better on wide tables
        },
        bodyStyles: {
            fontSize: 10,
            cellPadding: 4, // More padding fills the vertical space nicely
            halign: 'center'
        },
        alternateRowStyles: {
            fillColor: [245, 245, 245],
        },
        styles: {
            lineColor: [0, 0, 0],
            lineWidth: 0.2, // A thinner line weight (0.2) looks cleaner on full-width reports
        },
        // 3. Allow columns to grow. We keep column 0 left-aligned for names.
        columnStyles: {
            0: { halign: 'left', cellWidth: 'auto' }, 
            1: { cellWidth: 'auto' },
            2: { cellWidth: 'auto' },
            3: { cellWidth: 'auto' },
        },
    });

    doc.save('local-incidents-by-wmrc.pdf');
};

    const downloadFile = (data, fileName = 'download') => {
        const csvBlob = new Blob([data], { type: 'text/csv' });
        const csvUrl = URL.createObjectURL(csvBlob);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = csvUrl;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${fileName}.csv`;
        hiddenElement.click();
    };

    const ExcelLocalIncidentDProcessedByWmrc = async () => {
        try {
            let response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                { ...formattedPayload, export: 'Y', pagination: 'false' },
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            );
            downloadFile(response?.data, 'local-incidents-by-wmrc');
        } catch (error) {
            toasters({ type: 'error', message: error?.response?.data?.message });
        }
    };

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <>
                    <LocalIncidentsProcessedByWmrcTable
                        isLoading={isLoading}
                        data={data}
                        handleColumnSort={handleColumnSort}
                        downloadPDF={downloadPDF}
                        downloadExcel={ExcelLocalIncidentDProcessedByWmrc}
                        DisplayDate={formattedPayload}
                        reportData={formattedPayload}
                        reportCode={payload?.code || 'localIncidentsProcessedByWmrc'}
                        reportTableData={data}
                        generatePDFFunction={downloadPDF}
                    />
                </>
            )}
        </>
    );
};

export default LocalIncidentsProcessedByWmrc;
